import React, { useEffect, useRef, useState } from "react";
import {
  Form,
  Input,
  Spin,
  Select,
  InputNumber,
  Divider,
  DatePicker,
  Space,
} from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { save } from "../../../features/save/saveSlice";
import BreadCrumb from "../../../layout/BreadCrumb";
import {
  fetchCounties,
  fetchData,
  fetchLocation,
  fetchSubCounties,
  fetchSubLocation,
  fetchSubsidySource,
} from "../../../features/fetch/fetchSlice";
import CardLoading from "../../../components/CardLoading";
import dayjs from "dayjs";
import { normalizeDateToLocalYear } from "../../../utils";
import { schReclStatusData } from "../../../data";

const { TextArea } = Input;
const ReclaimedSchemeAdd = () => {
  const urlTechnology =
    "land_reclamation_technology/fetch_landreclamationtechnologys";
  const urlSoilType = "soil_type/fetch_soiltypes";
  const urlTypography = "typography/fetch_typographys";
  const urlFndingMode = "funding_mode/fetch_fundingmodes";
  const urlPathway = "api/v2/irrigationPathway/get";
  const urlWard = "ward/fetch_wards";
  const urlConstituency = "constituency/fetch_constituencys";

  const [form] = Form.useForm();
  const formRef = useRef(null);
  const { saving } = useSelector((state) => state.save);
  const {
    counties,
    subCounties,
    locations,
    subLocations,
    ward,
    constituency,
    subsidySources,
  } = useSelector((state) => state.fetch);
  const {
    loading,
    activeObjReclaimedScheme,
    dataTech,
    dataFundingMode,
    dataPathway,
    dataSoilType,
    dataTypography,
    dataConstituency,
    dataWard,
  } = useSelector((state) => ({
    activeObjReclaimedScheme: state.obj.activeObj["reclaimedScheme"],
    dataTech: state.fetch.data[urlTechnology] || [],
    dataFundingMode: state.fetch.data[urlFndingMode] || [],
    dataPathway: state.fetch.data[urlPathway] || [],
    dataSoilType: state.fetch.data[urlSoilType] || [],
    dataTypography: state.fetch.data[urlTypography] || [],

    dataWard: state.fetch.data[urlWard] || [],
    dataConstituency: state.fetch.data[urlConstituency] || [],
  }));

  const dispatch = useDispatch();
  const navigate = useNavigate();

  function handleCancel() {
    navigate("/rob/reclaimed-scheme");
  }

  const [schCountyId, setSchCountyId] = useState(
    activeObjReclaimedScheme?.schCountyId ?? ""
  );
  const [schConstId, setSchConstId] = useState(
    activeObjReclaimedScheme?.schConstId ?? ""
  );
  const [schSubCountyId, setSchSubCountyId] = useState(
    activeObjReclaimedScheme?.schSubCountyId ?? ""
  );
  const [schLocationId, setSchLocationId] = useState(
    activeObjReclaimedScheme?.schLocationId ?? ""
  );
  const [schSubLocationId, setSchSubLocationId] = useState(
    activeObjReclaimedScheme?.schSubLocationId ?? ""
  );
  const [schWardId, setSchWardId] = useState(
    activeObjReclaimedScheme?.schWardId ?? ""
  );

  const handleSchCountyChange = async (value) => {
    setSchCountyId(value);
    setSchConstId("");
    setSchSubCountyId("");
    setSchLocationId("");
    setSchSubLocationId("");
    setSchWardId("");

    form.setFieldsValue({
      schCountyId: value,
      schConstId: "",
      schSubCountyId: "",
      schLocationId: "",
      schSubLocationId: "",
      schWardId: "",
    });

    await dispatch(fetchSubCounties({ subCountyId: value }));
    await dispatch(fetchData({ url: urlConstituency, constCountyId: value }));
  };

  const handleSchConstituencyChange = async (value) => {
    setSchConstId(value);
    setSchWardId("");

    form.setFieldsValue({
      schConstId: value,
      schWardId: "",
    });

    await dispatch(fetchData({ url: urlWard, wdConstId: value }));
  };

  const handleSchSubCountyChange = async (value) => {
    setSchSubCountyId(value);
    setSchLocationId("");
    setSchSubLocationId("");

    form.setFieldsValue({
      schSubCountyId: value,
      schLocationId: "",
      schSubLocationId: "",
    });

    await dispatch(fetchLocation({ locSubId: value }));
  };

  const handleSchLocationChange = async (value) => {
    setSchLocationId(value);
    setSchSubLocationId("");

    form.setFieldsValue({
      schLocationId: value,
      schSubLocationId: "",
    });

    await dispatch(fetchSubLocation({ slocLocId: value }));
  };

  const handleSchSubLocationChange = (value) => {
    setSchSubLocationId(value);
    form.setFieldsValue({ schSubLocationId: value });
  };

  const handleSchWardChange = (value) => {
    setSchWardId(value);
    form.setFieldsValue({ schWardId: value });
  };

  const onFinish = async (data) => {
    data.schId = activeObjReclaimedScheme?.schId
      ? activeObjReclaimedScheme?.schId
      : null;
    data.schStartDate = normalizeDateToLocalYear(data?.schStartDate);
    data.schEndDate = normalizeDateToLocalYear(data?.schEndDate);
    data.schRevisedDate = normalizeDateToLocalYear(data?.schRevisedDate);

    data.schSourceFundsId = Array.isArray(data?.schSourceFundsId)
      ? data?.schSourceFundsId.join(",")
      : data?.schSourceFundsId;

    data.schPathwayId = Array.isArray(data?.schPathwayId)
      ? data?.schPathwayId.join(",")
      : data?.schPathwayId;

    data.schSoilTypeId = Array.isArray(data?.schSoilTypeId)
      ? data?.schSoilTypeId.join(",")
      : data?.schSoilTypeId;

    const res = await dispatch(
      save({
        ...data,
        url: "scheme/save_scheme",
      })
    );
    if (res?.payload?.success) {
      await toast.success(res?.payload?.message);
      await form.resetFields();
      await handleCancel();
    } else {
      toast.error(res?.payload?.message);
    }
  };

  useEffect(() => {
    form.setFieldsValue({
      ...activeObjReclaimedScheme,
      schStartDate: activeObjReclaimedScheme?.schStartDate
        ? dayjs(new Date(activeObjReclaimedScheme?.schStartDate))
        : null,
      schEndDate: activeObjReclaimedScheme?.schEndDate
        ? dayjs(new Date(activeObjReclaimedScheme?.schEndDate))
        : null,
      schRevisedDate: activeObjReclaimedScheme?.schRevisedDate
        ? dayjs(new Date(activeObjReclaimedScheme?.schRevisedDate))
        : null,
    });
    if (activeObjReclaimedScheme?.schSourceFundsId) {
      form.setFieldsValue({
        schSourceFundsId: activeObjReclaimedScheme?.schSourceFundsId
          ?.split(",")
          ?.map((b) => b?.trim()),
      });
    }
    if (activeObjReclaimedScheme?.schPathwayId) {
      form.setFieldsValue({
        schPathwayId: activeObjReclaimedScheme?.schPathwayId
          ?.split(",")
          ?.map((b) => b?.trim()),
        schSoilTypeId: activeObjReclaimedScheme?.schSoilTypeId
          ?.split(",")
          ?.map((b) => b?.trim()),
      });
    }
  }, [activeObjReclaimedScheme, form]);

  const initialFormValues = {
    ...activeObjReclaimedScheme,
    schStartDate: activeObjReclaimedScheme?.schStartDate
      ? dayjs(new Date(activeObjReclaimedScheme?.schStartDate))
      : null,
    schEndDate: activeObjReclaimedScheme?.schEndDate
      ? dayjs(new Date(activeObjReclaimedScheme?.schEndDate))
      : null,
    schRevisedDate: activeObjReclaimedScheme?.schRevisedDate
      ? dayjs(new Date(activeObjReclaimedScheme?.schRevisedDate))
      : null,
  };

  useEffect(() => {
    dispatch(fetchCounties());
    dispatch(
      fetchData({
        url: urlFndingMode,
        limit: null,
        start: 0,
      })
    );
    dispatch(
      fetchData({
        url: urlPathway,
        limit: null,
        start: 0,
      })
    );
  }, []);

  useEffect(() => {
    if (activeObjReclaimedScheme?.schId) {
      dispatch(
        fetchSubCounties({ subCountyId: activeObjReclaimedScheme?.schCountyId })
      );
      dispatch(
        fetchData({
          url: urlConstituency,
          constCountyId: activeObjReclaimedScheme?.schCountyId,
        })
      );
      dispatch(
        fetchData({
          url: urlWard,
          wdConstId: activeObjReclaimedScheme?.schConstId,
        })
      );
      dispatch(
        fetchLocation({ locSubId: activeObjReclaimedScheme?.schSubCountyId })
      );
      dispatch(
        fetchSubLocation({ slocLocId: activeObjReclaimedScheme?.schLocationId })
      );
    }
  }, [activeObjReclaimedScheme, form]);

  useEffect(() => {
    dispatch(fetchSubsidySource());
    dispatch(
      fetchData({
        url: urlTechnology,
        limit: null,
        start: 0,
      })
    );
    dispatch(
      fetchData({
        url: urlSoilType,
        limit: null,
        start: 0,
      })
    );
    dispatch(
      fetchData({
        url: urlTypography,
        limit: null,
        start: 0,
      })
    );
  }, []);

  const updateCategory = (size) => {
    if (size <= 100) return "Small";
    if (size > 100 && size <= 999) return "Medium";
    if (size >= 1000) return "Large";
    return "";
  };

  if (loading) return <CardLoading />;

  return (
    <div className="w-full ">
      <BreadCrumb
        breadcrumbs={[
          { title: "Reclaimed Scheme List", path: "/rob/reclaimed-scheme" },
          { title: "Reclaimed Scheme Add Form" },
        ]}
      />
      <div className="white_card mt-[2.06rem]">
        <div className="w-full">
          <Form
            layout="vertical"
            ref={formRef}
            name="control-ref"
            onFinish={onFinish}
            className=""
            style={{
              maxWidth: "100%",
            }}
            form={form}
            initialValues={initialFormValues}
            onValuesChange={(changedValues, allValues) => {
              if (changedValues?.schSize !== undefined) {
                const categoryValue = updateCategory(changedValues.schSize);
                form?.setFieldsValue({ schCategory: categoryValue });
              }
            }}
          >
            <div className="custom-title !bg-white !px-0">
              New Reclaimed scheme
            </div>
            <div className="grid lg:grid-cols-2 grid-cols-1 lg:gap-x-10">
              <Form.Item
                name="schName"
                label={"Project Name"}
                rules={[
                  {
                    required: true,
                    message: "Please add project name",
                  },
                ]}
              >
                <Input className="input" />
              </Form.Item>
              <Form.Item
                name="schStatus"
                className=""
                label={"Status"}
                rules={[
                  {
                    required: true,
                    message: "Please select status",
                  },
                ]}
              >
                <Select
                  className=""
                  allowClear
                  style={{
                    width: "100%",
                  }}
                  options={
                    schReclStatusData?.length > 0 &&
                    schReclStatusData?.map((item) => ({
                      value: item?.value,
                      label: item?.label,
                    }))
                  }
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option?.label?.toLocaleLowerCase() ?? "").includes(
                      input?.toLocaleLowerCase()
                    )
                  }
                />
              </Form.Item>
              <Form.Item
                name="schCountyId"
                label="County"
                rules={[{ required: true, message: "Please select county" }]}
              >
                <Select
                  allowClear
                  options={counties?.map((c) => ({
                    value: c?.countyId,
                    label: c?.countyName,
                  }))}
                  onChange={handleSchCountyChange}
                  showSearch
                  filterOption={(input, option) =>
                    option?.label?.toLowerCase()?.includes(input.toLowerCase())
                  }
                />
              </Form.Item>

              {schCountyId && (
                <>
                  <Form.Item
                    name="schConstId"
                    label="Constituency"
                    rules={[
                      { required: true, message: "Please select constituency" },
                    ]}
                  >
                    <Select
                      allowClear
                      options={dataConstituency?.map((c) => ({
                        value: c?.constId,
                        label: c?.constName,
                      }))}
                      onChange={handleSchConstituencyChange}
                      showSearch
                      filterOption={(input, option) =>
                        option?.label
                          ?.toLowerCase()
                          ?.includes(input.toLowerCase())
                      }
                    />
                  </Form.Item>

                  <Form.Item
                    name="schSubCountyId"
                    label="Subcounty"
                    rules={[
                      { required: true, message: "Please select subcounty" },
                    ]}
                  >
                    <Select
                      allowClear
                      options={subCounties?.map((s) => ({
                        value: s?.subId,
                        label: s?.subCountyName,
                      }))}
                      onChange={handleSchSubCountyChange}
                      showSearch
                      filterOption={(input, option) =>
                        option?.label
                          ?.toLowerCase()
                          ?.includes(input.toLowerCase())
                      }
                    />
                  </Form.Item>
                </>
              )}

              {schSubCountyId && (
                <Form.Item
                  name="schLocationId"
                  label="Location"
                  rules={[
                    { required: true, message: "Please select location" },
                  ]}
                >
                  <Select
                    allowClear
                    options={locations?.map((l) => ({
                      value: l?.locId,
                      label: l?.locLocation,
                    }))}
                    onChange={handleSchLocationChange}
                    showSearch
                    filterOption={(input, option) =>
                      option?.label
                        ?.toLowerCase()
                        ?.includes(input.toLowerCase())
                    }
                  />
                </Form.Item>
              )}

              {schLocationId && (
                <Form.Item
                  name="schSubLocationId"
                  label="Sublocation"
                  rules={[
                    { required: true, message: "Please select sublocation" },
                  ]}
                >
                  <Select
                    allowClear
                    options={subLocations?.map((sl) => ({
                      value: sl?.slocId,
                      label: sl?.slocLocation,
                    }))}
                    onChange={handleSchSubLocationChange}
                    showSearch
                    filterOption={(input, option) =>
                      option?.label
                        ?.toLowerCase()
                        ?.includes(input.toLowerCase())
                    }
                  />
                </Form.Item>
              )}

              {schConstId && (
                <Form.Item
                  name="schWardId"
                  label="Ward"
                  rules={[{ required: true, message: "Please select ward" }]}
                >
                  <Select
                    allowClear
                    options={dataWard?.map((w) => ({
                      value: w?.wdId,
                      label: w?.wdWard,
                    }))}
                    onChange={handleSchWardChange}
                    showSearch
                    filterOption={(input, option) =>
                      option?.label
                        ?.toLowerCase()
                        ?.includes(input.toLowerCase())
                    }
                  />
                </Form.Item>
              )}

              <Form.Item
                name="schLat"
                label={"Latitude (Decimal degrees, WGS84)"}
                rules={[
                  {
                    required: true,
                    message: "Please add latitude",
                  },
                ]}
              >
                <InputNumber
                  parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                  className="input flex"
                />
              </Form.Item>
              <Form.Item
                name="schLong"
                label={"Longitude (Decimal degrees, WGS84)"}
                rules={[
                  {
                    required: true,
                    message: "Please add longitude",
                  },
                ]}
              >
                <InputNumber
                  parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                  className="input flex"
                />
              </Form.Item>
             
              <Form.Item
                name="schSize"
                label={"Size (Acres)"}
                rules={[
                  {
                    required: true,
                    message: "Please add size",
                  },
                ]}
              >
                <InputNumber
                  parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                  className="input flex"
                />
              </Form.Item>

              <Form.Item
                name="schCategory"
                label={"Category"}
                rules={[
                  {
                    required: true,
                    message: "Please add category",
                  },
                ]}
              >
                <InputNumber
                  disabled
                  parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                  className="input flex"
                />
              </Form.Item>
            </div>

            <div className="custom-title !bg-white !px-0">
              Contractor Details
            </div>
            <div className="grid lg:grid-cols-2 grid-cols-1 lg:gap-x-10">
              <Form.Item
                name="schContName"
                label={"Contractor Name"}
                rules={[
                  {
                    required: true,
                    message: "Please add contractor name",
                  },
                ]}
              >
                <Input className="input" />
              </Form.Item>
              <Form.Item
                label="Contract Start Date"
                name="schStartDate"
                rules={[
                  {
                    required: true,
                    message: "Contract start date is required",
                  },
                ]}
              >
                <DatePicker format={"YYYY MMM DD"} className="input" />
              </Form.Item>
              <Form.Item
                label="Contract End Date"
                name="schEndDate"
                rules={[
                  {
                    required: true,
                    message: "Contract end date is required",
                  },
                ]}
              >
                <DatePicker format={"YYYY MMM DD"} className="input" />
              </Form.Item>
              <Form.Item
                label="Revised End Date"
                name="schRevisedDate"
                rules={[
                  {
                    required: false,
                    message: "Revised end date is required",
                  },
                ]}
              >
                <DatePicker format={"YYYY MMM DD"} className="input" />
              </Form.Item>
              <Form.Item
                label="Source of Funds"
                name="schSourceFundsId"
                rules={[
                  { required: true, message: "Source of Funds is required" },
                ]}
              >
                <Select
                  mode="multiple"
                  allowClear
                  showSearch
                  dropdownRender={(menu) => (
                    <>
                      <Space style={{ padding: "8px 8px 4px" }}>
                        <span className="select-info mt-1">
                          Choose one or more of the following sources
                        </span>
                      </Space>
                      <Divider style={{ margin: "8px 0" }} />
                      {menu}
                    </>
                  )}
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option?.children?.toLowerCase() ?? "").includes(
                      input.toLowerCase()
                    )
                  }
                  tagRender={(props) => {
                    const { label, closable, onClose } = props;
                    return (
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          padding: "4px 8px",
                          margin: "2px",
                          borderRadius: "4px",
                          background: "#f5f5f5",
                        }}
                      >
                        {label}
                        {closable && (
                          <span
                            onClick={onClose}
                            style={{
                              marginLeft: "8px",
                              cursor: "pointer",
                              color: "red",
                            }}
                          >
                            ×
                          </span>
                        )}
                      </span>
                    );
                  }}
                  dropdownStyle={{ maxHeight: 400, overflowY: "auto" }}
                >
                  {subsidySources?.map((item) => (
                    <Select.Option
                      key={item?.subsidySourceId}
                      value={item?.subsidySourceId}
                    >
                      {item?.subsidySourceName}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item
                name="schFundingModeId"
                className=""
                label={"Mode of Funding"}
                rules={[
                  {
                    required: true,
                    message: "Please select mode of funding",
                  },
                ]}
              >
                <Select
                  className=""
                  allowClear
                  style={{
                    width: "100%",
                  }}
                  options={
                    dataFundingMode?.length > 0 &&
                    dataFundingMode?.map((item) => ({
                      value: item?.fmId,
                      label: item?.fmType,
                    }))
                  }
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option?.label?.toLocaleLowerCase() ?? "").includes(
                      input?.toLocaleLowerCase()
                    )
                  }
                />
              </Form.Item>
              <Form.Item
                name="schExpenditureToDate"
                label={"Actual Expenditure to Date"}
                rules={[
                  {
                    required: true,
                    message: "Please add actual expenditure to date",
                  },
                ]}
              >
                <InputNumber
                  min={0}
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                  placeholder="Enter value"
                  className="input flex"
                />
              </Form.Item>
              <Form.Item
                name="schDisbursedAmount"
                label={"Disbursed Amount"}
                rules={[
                  {
                    required: true,
                    message: "Please add disbursed amount",
                  },
                ]}
              >
                <InputNumber
                  min={0}
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                  placeholder="Enter value"
                  className="input flex"
                />
              </Form.Item>
            </div>

            <div className="custom-title !bg-white !px-0">
              Other Information
            </div>
            <div className="grid lg:grid-cols-2 grid-cols-1 lg:gap-x-10">
              <Form.Item
                name="schWorkScope"
                label={"Scopes of Work"}
                rules={[
                  {
                    required: true,
                    message: "Please add scope of work",
                  },
                ]}
              >
                <TextArea rows={5} className="text_area" />
              </Form.Item>
              <Form.Item
                name="schLandUse"
                label={"Land Use"}
                rules={[
                  {
                    required: true,
                    message: "Please add land use",
                  },
                ]}
              >
                <Input className="input" />
              </Form.Item>
              <Form.Item
                name="schProductivityArea"
                label={"Productivity per kg/acre"}
                rules={[
                  {
                    required: true,
                    message: "Please add productivity per kg/acre",
                  },
                ]}
              >
                <Input className="input" />
              </Form.Item>
              <Form.Item
                name="schLandRecId"
                className=""
                label={"Land Reclamation Technology Used"}
                rules={[
                  {
                    required: true,
                    message: "Please select land reclamation technology used",
                  },
                ]}
              >
                <Select
                  className=""
                  allowClear
                  style={{
                    width: "100%",
                  }}
                  options={
                    dataTech?.length > 0 &&
                    dataTech?.map((item) => ({
                      value: item?.lrtId,
                      label: item?.lrtType,
                    }))
                  }
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option?.label?.toLocaleLowerCase() ?? "").includes(
                      input?.toLocaleLowerCase()
                    )
                  }
                />
              </Form.Item>
              <Form.Item
                name="schHousehold"
                label={"Beneficiary Households"}
                rules={[
                  {
                    required: true,
                    message: "Please add beneficiary households",
                  },
                ]}
              >
                <InputNumber
                  min={0}
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                  placeholder="Enter value"
                  className="input flex"
                />
              </Form.Item>

              <Form.Item
                label={"Pathways"}
                name="schPathwayId"
                rules={[
                  {
                    required: true,
                    message: "Please add pathways",
                  },
                ]}
              >
                <Select
                  mode="multiple"
                  allowClear
                  showSearch
                  dropdownRender={(menu) => (
                    <>
                      <Space style={{ padding: "8px 8px 4px" }}>
                        <span className="select-info mt-1">
                          Choose one or more of the following pathways
                        </span>
                      </Space>
                      <Divider style={{ margin: "8px 0" }} />
                      {menu}
                    </>
                  )}
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option?.children?.toLowerCase() ?? "").includes(
                      input.toLowerCase()
                    )
                  }
                  dropdownStyle={{ maxHeight: 400, overflowY: "auto" }}
                >
                  {dataPathway?.length > 0 &&
                    dataPathway?.map((item) => (
                      <Select.Option
                        key={item?.irrigationPathwayId}
                        value={item?.irrigationPathwayId}
                      >
                        {item?.irrigationPathwayName}
                      </Select.Option>
                    ))}
                </Select>
              </Form.Item>

              <Form.Item
                label={"Soil Type"}
                name="schSoilTypeId"
                rules={[
                  {
                    required: true,
                    message: "Please select soil type",
                  },
                ]}
              >
                <Select
                  mode="multiple"
                  allowClear
                  showSearch
                  dropdownRender={(menu) => (
                    <>
                      <Space style={{ padding: "8px 8px 4px" }}>
                        <span className="select-info mt-1">
                          Choose one or more of the following soil types
                        </span>
                      </Space>
                      <Divider style={{ margin: "8px 0" }} />
                      {menu}
                    </>
                  )}
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option?.children?.toLowerCase() ?? "").includes(
                      input.toLowerCase()
                    )
                  }
                  dropdownStyle={{ maxHeight: 400, overflowY: "auto" }}
                >
                  {dataSoilType?.length > 0 &&
                    dataSoilType?.map((item) => (
                      <Select.Option key={item?.soilId} value={item?.soilId}>
                        {item?.soilType}
                      </Select.Option>
                    ))}
                </Select>
              </Form.Item>

              <Form.Item
                name="schTypographyId"
                className=""
                label={"Topography"}
                rules={[
                  {
                    required: true,
                    message: "Please select topography",
                  },
                ]}
              >
                <Select
                  className=""
                  allowClear
                  style={{
                    width: "100%",
                  }}
                  options={
                    dataTypography?.length > 0 &&
                    dataTypography?.map((item) => ({
                      value: item?.typoId,
                      label: item?.typoTypography,
                    }))
                  }
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option?.label?.toLocaleLowerCase() ?? "").includes(
                      input?.toLocaleLowerCase()
                    )
                  }
                />
              </Form.Item>

              <Form.Item
                name="schCost"
                label={"Cost of Land Reclamation"}
                rules={[
                  {
                    required: true,
                    message: "Please add Cost of Land Reclamation",
                  },
                ]}
              >
                <InputNumber
                  min={0}
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                  className="input flex"
                />
              </Form.Item>
            </div>

            <div className="flex justify-between mt-[48px] ">
              <div className="justify-start"></div>
              <div className="justify-end flex items-center mb-[58px] gap-x-5">
                <div className="w-[150px]">
                  <button
                    type="button"
                    key="back"
                    onClick={handleCancel}
                    className="cstm-btn"
                  >
                    Cancel
                  </button>
                </div>

                <div className="w-[150px]">
                  <button key="submit" type="submit" className="cstm-btn-2">
                    {saving ? (
                      <Spin />
                    ) : activeObjReclaimedScheme?.usrId ? (
                      "Update"
                    ) : (
                      "Save"
                    )}
                  </button>
                </div>
              </div>
            </div>
          </Form>
        </div>
      </div>
    </div>
  );
};
export default ReclaimedSchemeAdd;
